/**
 * PostMessage utility for communicating with parent page when embedded
 * Based on PROD-655 requirements
 */

class PostMessageService {
    constructor() {
        this.parentOrigin = this.determineParentOrigin();
        this.isEmbedded = this.checkIfEmbedded();
    }

    /**
     * Determine the allowed parent origin from URL params or document referrer
     */
    determineParentOrigin() {
        try {
            const url = new URL(window.location.href);
            const parentOrigin = url.searchParams.get("parent_origin");

            if (parentOrigin) {
                return parentOrigin;
            }

            // Fallback to document referrer origin
            if (document.referrer) {
                return new URL(document.referrer).origin;
            }

            return null;
        } catch (error) {
            console.warn("Failed to determine parent origin:", error);
            return null;
        }
    }

    /**
     * Check if we're running in an embedded context
     */
    checkIfEmbedded() {
        return window.parent && window.parent !== window;
    }

    /**
     * Send a message to the parent window
     * @param {string} type - Event type (form_viewed, form_closed_viewed, submit_success)
     * @param {string} formKey - Form key from URL params
     * @param {Object} formSchema - Form schema containing name and other properties
     */
    postToParent(type, formKey, formSchema = {}) {
        if (!this.parentOrigin || !this.isEmbedded) {
            return;
        }

        const payload = {
            source: "roster:app-form",
            version: "1.0",
            type,
            formKey: formKey || "",
            formName: formSchema.name || "",
        };

        try {
            window.parent.postMessage(payload, this.parentOrigin);
        } catch (error) {
            console.error("Failed to post message to parent:", error);
        }
    }

    /**
     * Emit form viewed event
     * @param {string} formKey - Form key from URL params
     * @param {Object} formSchema - Form schema
     * @param {boolean} isClosed - Whether form is in closed state
     */
    emitFormViewed(formKey, formSchema, isClosed = false) {
        const type = isClosed ? "form_closed_viewed" : "form_viewed";
        this.postToParent(type, formKey, formSchema);
    }

    /**
     * Emit form submission success event
     * @param {string} formKey - Form key from URL params
     * @param {Object} formSchema - Form schema
     */
    emitSubmitSuccess(formKey, formSchema) {
        this.postToParent("submit_success", formKey, formSchema);
    }
}

// Create singleton instance
const postMessageService = new PostMessageService();

export default postMessageService;
